<template>
  <div class="pls-material-preparation-container">
    <header class="header">
      <div class="back-button" @click="goBack">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </div>
      <h1 class="page-title">PLS备料管理</h1>
    </header>

    <main class="content">
      <!-- 搜索筛选区域 -->
      <div class="search-filter-container">
        <div class="search-container">
          <input
            type="text"
            v-model="searchKeyword"
            placeholder="搜索：供应商空格物料编码，例：供应商A 123456"
            class="search-input"
            @keyup.enter="searchLogs"
          />
          <button class="search-btn" @click="searchLogs">搜索</button>
        </div>

        <div class="filter-container">
          <div class="filter-nav">
            <div
              v-for="filter in filterOptions"
              :key="filter.value"
              :class="[
                'filter-item',
                { active: activeFilter === filter.value },
              ]"
              @click="setFilter(filter.value)"
            >
              {{ filter.label }}
            </div>
          </div>

          <div class="filter-nav">
            <div
              v-for="filter in emergencyFilterOptions"
              :key="filter.value"
              :class="[
                'filter-item',
                { active: activeEmergencyFilter === filter.value },
              ]"
              @click="setEmergencyFilter(filter.value)"
            >
              {{ filter.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- 日志列表 -->
      <div class="log-list">
        <div v-if="loadingLogs" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">加载中...</div>
        </div>

        <div
          v-else-if="logs.length"
          v-for="log in logs"
          :key="log.id"
          class="log-card"
        >
          <div class="log-card-header" @click="toggleLogExpand(log.id)">
            <div class="log-header-top">
              <div class="log-pls-no">
                PLS单号：{{ log.plsNo || log.wareOutNo || "无订单号" }}
              </div>
              <div :class="['log-status', `status-${log.status}`]">
                {{ getStatusText(log.status) }}
              </div>
            </div>
            <div class="log-basic-info">
              <span>供应商：{{ getSupplierName(log) }}</span>
              <span>{{ formatTime(log.createTime) }}</span>
            </div>
            <div class="log-factory-info">
              主机厂：{{ log.factoryName || "暂无" }}
            </div>
          </div>

          <div class="material-section">
            <div class="material-list">
              <div
                v-for="(material, index) in log.wareOutDetailList"
                :key="index"
                class="material-item"
                :title="`${material.materialNo} - ${
                  material.materialDesc
                } - 数量: ${Number(material.wareOutNum).toFixed(2)}`"
              >
                <div class="material-status-code-row">
                  <div class="material-code-wrapper">
                    <span class="material-code-label">编码：</span>
                    <span
                      class="material-no"
                      @click.stop="handleMaterialCodeClick(material, log)"
                      >{{ material.materialNo }}</span
                    >
                  </div>
                  <div class="material-status-wrapper">
                    <span
                      :class="[
                        'material-status',
                        {
                          'status-pending': material.status === 0,
                          'status-preparing': material.status === 1,
                          'status-ready': material.status === 2,
                        },
                      ]"
                    >
                      {{
                        material.status === 0
                          ? "未备料"
                          : material.status === 1
                          ? "备料中"
                          : "已备料"
                      }}
                    </span>
                  </div>
                </div>

                <div class="material-desc-row">
                  <span class="material-desc"
                    >描述：{{ material.materialDesc }}</span
                  >
                </div>

                <div class="material-quantity-carton-row">
                  <div class="material-quantity-wrapper">
                    <span class="quantity-label">数量：</span>
                    <span class="material-quantity">{{
                      Number(material.wareOutNum).toFixed(2)
                    }}</span>
                  </div>

                  <div class="carton-info-wrapper">
                    <span class="carton-label">箱数/尾箱：</span>
                    <span
                      >{{ Math.floor(material.cartonNum || 0) }} /
                      {{
                        (material.wareOutNum || 0) -
                        Math.floor(material.cartonNum || 0) *
                          (material.packageNum || 1)
                      }}</span
                    >
                  </div>

                  <div class="material-actions">
                    <button
                      v-if="material.status === 0 || material.status === 1"
                      class="allocate-btn"
                      @click.stop="openAllocationModal(material, log)"
                    >
                      分配
                    </button>
                    <button
                      v-if="material.status === 0 || material.status === 1"
                      class="confirm-btn"
                      @click.stop="openConfirmModal(material)"
                      :disabled="
                        submittingMaterials.includes(
                          material.wareOutDetailId ||
                            material.id ||
                            material.materialId,
                        )
                      "
                    >
                      {{
                        submittingMaterials.includes(
                          material.wareOutDetailId ||
                            material.id ||
                            material.materialId,
                        )
                          ? "提交中..."
                          : "备料完成"
                      }}
                    </button>
                    <button
                      v-if="
                        getFailStatus(material.id || material.materialId) &&
                        !submittingMaterials.includes(
                          material.id || material.materialId,
                        )
                      "
                      class="retry-btn"
                      @click.stop="handleRetry(material)"
                    >
                      重试
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-if="
                  !log.wareOutDetailList || log.wareOutDetailList.length === 0
                "
                class="empty-material"
              >
                暂无物料信息
              </div>
            </div>
          </div>

          <div v-if="log.failDesc" class="fail-section">
            <span class="section-label">失败原因：</span>
            <span class="fail-desc">{{ log.failDesc }}</span>
          </div>
        </div>

        <div v-else class="no-data">
          <img src="@/assets/无数据.svg" alt="无数据" class="no-data-img" />
          <p>暂无备料日志数据</p>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="total > 0" class="pagination">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          上一页
        </button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          下一页
        </button>
      </div>

      <!-- 物料库存弹窗 -->
      <teleport to="body">
        <div
          v-if="stockModalVisible"
          class="stock-modal-overlay"
          @click="closeStockModal"
        >
          <div class="stock-modal" @click.stop>
            <div class="stock-modal-header">
              <h3>物料库存信息</h3>
              <button class="close-btn" @click.stop="closeStockModal">×</button>
            </div>
            <div class="stock-modal-body">
              <div v-if="loadingStock" class="loading-container">
                <div class="loading-spinner"></div>
                <div class="loading-text">加载库存中...</div>
              </div>
              <div v-else class="stock-info">
                <div class="stock-item">
                  <span class="label">物料编码：</span>
                  <span class="value">{{
                    currentStockData?.materialNo || "N/A"
                  }}</span>
                </div>
                <div class="stock-item">
                  <span class="label">库存数量：</span>
                  <span class="value">{{
                    currentStockData?.stockNum || 0
                  }}</span>
                </div>
                <div class="stock-item">
                  <span class="label">良品数量：</span>
                  <span class="value">{{
                    currentStockData?.goodStockNum || 0
                  }}</span>
                </div>
                <div class="stock-item">
                  <span class="label">不良品数量：</span>
                  <span class="value">{{
                    currentStockData?.badStockNum || 0
                  }}</span>
                </div>
                <div
                  v-if="
                    !currentStockData ||
                    Object.keys(currentStockData).length === 0
                  "
                  class="no-data"
                >
                  暂无库存数据
                </div>
              </div>
            </div>
            <div class="stock-modal-footer">
              <button class="confirm-btn" @click.stop="closeStockModal">
                确认
              </button>
            </div>
          </div>
        </div>
      </teleport>

      <!-- 备料确认弹窗 -->
      <teleport to="body">
        <div
          v-if="confirmModalVisible"
          class="stock-modal-overlay"
          @click="cancelSubmitMaterialStatus"
        >
          <div class="stock-modal" @click.stop>
            <div class="stock-modal-header">
              <h3>备料确认</h3>
              <button class="close-btn" @click="cancelSubmitMaterialStatus">
                ×
              </button>
            </div>
            <div class="stock-modal-body">
              <div class="stock-info">
                <div class="stock-item">
                  <span class="label">物料编码：</span>
                  <span class="value">{{
                    currentMaterial?.materialNo || "N/A"
                  }}</span>
                </div>
                <div class="stock-item">
                  <span class="label">物料描述：</span>
                  <span class="value">{{
                    currentMaterial?.materialDesc || "N/A"
                  }}</span>
                </div>
                <div class="stock-item">
                  <span class="label">备料数量：</span>
                  <span class="value">{{
                    currentMaterial?.wareOutNum || 0
                  }}</span>
                </div>
                <div class="stock-item">
                  <span class="label">箱数/尾箱：</span>
                  <span
                    >{{ Math.floor(currentMaterial?.cartonNum || 0) }} /
                    {{
                      (currentMaterial?.wareOutNum || 0) -
                      Math.floor(currentMaterial?.cartonNum || 0) *
                        (currentMaterial?.packageNum || 1)
                    }}</span
                  >
                </div>
              </div>
              <div class="confirm-tip">确定要标记该物料为备料完成吗？</div>
            </div>
            <div class="stock-modal-footer">
              <button class="cancel-btn" @click="cancelSubmitMaterialStatus">
                取消
              </button>
              <button
                class="confirm-btn"
                @click="confirmSubmitMaterialStatus"
                :disabled="
                  submittingMaterials.includes(
                    currentMaterial.id || currentMaterial.materialId,
                  )
                "
              >
                <span
                  v-if="
                    submittingMaterials.includes(
                      currentMaterial.id || currentMaterial.materialId,
                    )
                  "
                  class="loading-spinner"
                ></span>
                {{
                  submittingMaterials.includes(
                    currentMaterial.id || currentMaterial.materialId,
                  )
                    ? "提交中..."
                    : "确认"
                }}
              </button>
            </div>
          </div>
        </div>
      </teleport>

      <!-- 分配用户弹窗 -->
      <teleport to="body">
        <div
          v-if="allocationModalVisible"
          class="stock-modal-overlay"
          @click="closeAllocationModal"
        >
          <div class="stock-modal" @click.stop>
            <div class="stock-modal-header">
              <h3>分配备料员</h3>
              <button class="close-btn" @click="closeAllocationModal">×</button>
            </div>
            <div class="stock-modal-body">
              <div class="stock-info">
                <div class="stock-item">
                  <span class="label">物料编码：</span>
                  <span class="value">{{
                    currentAllocationMaterial?.materialNo || "N/A"
                  }}</span>
                </div>
              </div>

              <div class="user-list-container">
                <h4>选择备料员</h4>
                <div v-if="loadingUsers" class="loading-container">
                  <div class="loading-spinner"></div>
                  <div class="loading-text">加载用户中...</div>
                </div>
                <div v-else class="user-list">
                  <div
                    v-for="user in usersList"
                    :key="user.id"
                    :class="[
                      'user-item',
                      { active: selectedUserId === user.id },
                    ]"
                    @click="selectedUserId = user.id"
                  >
                    <div class="user-name">{{ user.name }}</div>
                    <div class="user-account">{{ user.username }}</div>
                  </div>
                  <div v-if="usersList.length === 0" class="empty-users">
                    暂无备料员数据
                  </div>
                </div>
              </div>
            </div>
            <div class="stock-modal-footer">
              <button class="cancel-btn" @click="closeAllocationModal">
                取消
              </button>
              <button
                class="confirm-btn"
                @click="confirmAllocation"
                :disabled="!selectedUserId"
              >
                确认分配
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import * as materialPreparationApi from "../../api/materialPreparation";
import { getUserId } from "@/utils/auth";

export default {
  name: "PlsMaterialPreparation",
  setup() {
    const router = useRouter();

    // 基础状态
    const logs = ref([]);
    const total = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const searchKeyword = ref("");
    const activeFilter = ref("1");
    const activeEmergencyFilter = ref("0");
    const loadingLogs = ref(false);
    const expandedLogIds = ref([]);

    // 弹窗状态
    const loadingStock = ref(false);
    const stockModalVisible = ref(false);
    const currentStockData = ref(null);
    const submittingMaterials = ref([]);
    const confirmModalVisible = ref(false);
    const currentMaterial = ref({});
    const allocationModalVisible = ref(false);
    const loadingUsers = ref(false);
    const usersList = ref([]);
    const selectedUserId = ref("");
    const currentAllocationMaterial = ref({});

    // 轮询和WebSocket相关
    const pollTimers = ref({});
    let webSocket = null;
    // 初始化当前操作人ID
    const currentOperatorId = ref(getUserId() || ""); // 从用户信息中获取当前用户ID

    // 筛选选项
    const filterOptions = ref([
      { label: "全部", value: "all" },
      { label: "备料中", value: "1" },
      { label: "备料完成", value: "2" },
    ]);
    const emergencyFilterOptions = ref([
      { label: "正常", value: "0" },
      { label: "紧急", value: "1" },
      { label: "特急", value: "2" },
    ]);

    // 计算总页数
    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    // 获取备料日志列表
    const getLogs = async () => {
      try {
        loadingLogs.value = true;
        let supplierName = "";
        let materialNo = "";

        if (searchKeyword.value.trim()) {
          const parts = searchKeyword.value.split(/\s+/, 2);
          supplierName = parts[0] || "";
          materialNo = parts[1] || "";
        }

        const params = {
          current: currentPage.value,
          size: pageSize.value,
          supplierName,
          materialNo,
          planStatus: activeFilter.value === "all" ? "" : activeFilter.value,
          emergencyLevel: activeEmergencyFilter.value,
        };

        const res = await materialPreparationApi.sendPlsLog(params);
        logs.value = res.data.records || [];
        total.value = res.data.total || 0;
        // 调试：打印物料字段，确认wareOutDetail相关字段名
        console.log(
          "最新物料列表:",
          logs.value.map((log) => ({
            logId: log.id,
            materials: log.wareOutDetailList.map((m) => ({
              materialNo: m.materialNo,
              id: m.id,
              wareOutDetailId: m.wareOutDetailId,
              wareOutDetail: m.wareOutDetail, // 兼容可能的字段名
            })),
          })),
        );
      } catch (error) {
        console.error("加载备料日志失败:", error);
        logs.value = [];
        total.value = 0;
      } finally {
        loadingLogs.value = false;
      }
    };

    // 搜索日志
    const searchLogs = () => {
      currentPage.value = 1;
      getLogs();
    };

    // 设置状态筛选
    const setFilter = (filter) => {
      activeFilter.value = filter;
      currentPage.value = 1;
      getLogs();
    };

    // 设置紧急程度筛选
    const setEmergencyFilter = (filter) => {
      activeFilter.value = filter;
      currentPage.value = 1;
      getLogs();
    };

    // 展开/折叠日志详情
    const toggleLogExpand = (logId) => {
      const index = expandedLogIds.value.indexOf(logId);
      if (index > -1) {
        expandedLogIds.value.splice(index, 1);
      } else {
        expandedLogIds.value.push(logId);
      }
    };

    // 获取供应商名称
    const getSupplierName = (log) => {
      if (log.supplierName) return log.supplierName;
      if (log.wareOutDetailList && log.wareOutDetailList.length > 0) {
        return log.wareOutDetailList[0].supplierName || "未知供应商";
      }
      return "未知供应商";
    };

    // 分页切换
    const changePage = (page) => {
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
      getLogs();
    };

    // 格式化时间
    const formatTime = (time) => {
      if (!time) return "";
      const date = new Date(time);
      return date.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    // 获取状态文本
    const getStatusText = (status) => {
      switch (Number(status)) {
        case 0:
          return "未开始";
        case 1:
          return "备料中";
        case 2:
          return "已完成";
        default:
          return "未知状态";
      }
    };

    // 打开分配用户弹窗
    const openAllocationModal = async (material) => {
      try {
        currentAllocationMaterial.value = { ...material };
        allocationModalVisible.value = true;
        loadingUsers.value = true;

        const res = await materialPreparationApi.getUsersList({
          current: 1,
          size: 100,
        });
        usersList.value = res.data.records || [];
        selectedUserId.value = "";
      } catch (error) {
        console.error("加载用户列表失败:", error);
        usersList.value = [];
      } finally {
        loadingUsers.value = false;
      }
    };

    // 确认分配用户
    const confirmAllocation = async () => {
      try {
        await materialPreparationApi.allocationUser(
          currentAllocationMaterial.value.wareOutId,
          selectedUserId.value,
        );
        allocationModalVisible.value = false;
        // 仅过滤数据，不刷新列表
        const targetWareOutId = currentAllocationMaterial.value.wareOutId;
        logs.value = logs.value
          .map((log) => {
            if (log.id === targetWareOutId) {
              return {
                ...log,
                wareOutDetailList: log.wareOutDetailList.filter(
                  (m) => m.id !== currentAllocationMaterial.value.id,
                ),
              };
            }
            return log;
          })
          .filter((log) => log.wareOutDetailList.length > 0);
        nextTick(() => {
          logs.value = [...logs.value];
        });
      } catch (error) {
        console.error("分配用户失败:", error);
      }
    };

    // 关闭分配用户弹窗
    const closeAllocationModal = () => {
      allocationModalVisible.value = false;
      selectedUserId.value = "";
    };

    // 查看物料库存
    const handleMaterialCodeClick = async (material, log) => {
      try {
        stockModalVisible.value = true;
        loadingStock.value = true;
        currentStockData.value = null;

        const params = {
          materialId: material.materialId,
          supplierId:
            log.supplierId || log.wareOutDetailList?.[0]?.supplierId || "",
        };

        const res = await materialPreparationApi.getStock(params);
        currentStockData.value = {
          materialNo: material.materialNo,
          stockNum: res.data.stockNum || res.data.total || 0,
          goodStockNum: res.data.goodStockNum || res.data.qualified || 0,
          badStockNum: res.data.badStockNum || res.data.unqualified || 0,
        };
      } catch (error) {
        console.error("获取库存失败:", error);
        currentStockData.value = { materialNo: material.materialNo };
      } finally {
        loadingStock.value = false;
      }
    };

    // 关闭库存弹窗
    const closeStockModal = () => {
      stockModalVisible.value = false;
      loadingStock.value = false;
      currentStockData.value = null;
    };

    // 打开备料确认弹窗
    const openConfirmModal = (material) => {
      currentMaterial.value = { ...material };
      confirmModalVisible.value = true;
    };

    // 取消备料提交
    const cancelSubmitMaterialStatus = () => {
      confirmModalVisible.value = false;
      currentMaterial.value = {};
    };

    // 获取失败状态
    const getFailStatus = (materialId) => {
      return localStorage.getItem(`pls_fail_${materialId}`) === "true";
    };

    // 轮询物料状态（兜底）- 仅前端过滤，不刷新
    const startPollMaterialStatus = (materialId) => {
      if (pollTimers.value[materialId])
        clearInterval(pollTimers.value[materialId]);

      let pollCount = 0;
      pollTimers.value[materialId] = setInterval(async () => {
        try {
          const res = await materialPreparationApi.getMaterialStatus(
            materialId,
          );
          if (res.data.planStatus === 2) {
            clearInterval(pollTimers.value[materialId]);
            delete pollTimers.value[materialId];
            submittingMaterials.value = submittingMaterials.value.filter(
              (id) => id !== materialId,
            );
            localStorage.removeItem(`pls_fail_${materialId}`);

            // 纯前端过滤：匹配到则移除，无页面刷新
            logs.value = logs.value
              .map((log) => {
                const remainMaterials = log.wareOutDetailList.filter(
                  (material) => {
                    const matId =
                      material.id ||
                      material.wareOutDetailId ||
                      material.wareOutDetail;
                    return matId !== materialId;
                  },
                );
                return { ...log, wareOutDetailList: remainMaterials };
              })
              .filter((log) => log.wareOutDetailList.length > 0);

            // 强制响应式更新，无刷新
            nextTick(() => {
              logs.value = [...logs.value];
            });
          }

          if (pollCount >= 10) {
            clearInterval(pollTimers.value[materialId]);
            delete pollTimers.value[materialId];
            submittingMaterials.value = submittingMaterials.value.filter(
              (id) => id !== materialId,
            );
            localStorage.setItem(`pls_fail_${materialId}`, "true");
          }
          pollCount++;
        } catch (error) {
          console.error("轮询物料状态失败:", error);
          pollCount++;
          if (pollCount >= 10) {
            clearInterval(pollTimers.value[materialId]);
            delete pollTimers.value[materialId];
          }
        }
      }, 3000);
    };

    // 提交备料状态（带重试）
    const submitWithRetry = async (materialId, retryCount = 0) => {
      const maxRetry = 3;
      try {
        console.log("提交备料完成，materialId:", materialId);
        await materialPreparationApi.updateMaterialStatus({ id: materialId });
        console.log("备料完成提交成功，materialId:", materialId);
        // WebSocket正常时无需轮询，由推送触发更新
        if (!webSocket || webSocket.readyState !== WebSocket.OPEN) {
          console.log("WebSocket未连接，启动轮询:", materialId);
          startPollMaterialStatus(materialId);
        }
      } catch (error) {
        console.error("提交备料失败:", error);
        if (retryCount < maxRetry) {
          const delay = Math.pow(2, retryCount) * 1000;
          setTimeout(() => submitWithRetry(materialId, retryCount + 1), delay);
        } else {
          submittingMaterials.value = submittingMaterials.value.filter(
            (id) => id !== materialId,
          );
          localStorage.setItem(`pls_fail_${materialId}`, "true");
        }
      }
    };

    // 确认备料提交
    const confirmSubmitMaterialStatus = async () => {
      // 兼容多字段：优先取wareOutDetailId，其次id/materialId
      const materialId =
        currentMaterial.value.wareOutDetailId ||
        currentMaterial.value.wareOutDetail ||
        currentMaterial.value.id ||
        currentMaterial.value.materialId;
      if (!materialId) return;

      submittingMaterials.value.push(materialId);
      confirmModalVisible.value = false;
      currentMaterial.value = {};

      await submitWithRetry(materialId);
    };

    // 重试提交
    const handleRetry = (material) => {
      const materialId =
        material.wareOutDetailId ||
        material.wareOutDetail ||
        material.id ||
        material.materialId;
      localStorage.removeItem(`pls_fail_${materialId}`);
      submittingMaterials.value.push(materialId);
      submitWithRetry(materialId);
    };

    // 返回上一页
    const goBack = () => {
      router.back();
    };

    // 初始化WebSocket - 核心：纯前端无感过滤
    const initWebSocket = () => {
      if (webSocket) webSocket.close();

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${protocol}//${window.location.host}/api/ws/simple?userId=${currentOperatorId.value}`;

      try {
        webSocket = new WebSocket(wsUrl);

        webSocket.onopen = () => {
          console.log("WebSocket连接成功:", wsUrl);
        };

        webSocket.onmessage = async (event) => {
          console.log("收到WebSocket消息:", event.data);
          try {
            // 自定义JSON解析函数，处理大整数精度问题
            const parseJSONWithBigInt = (str) => {
              // 使用正则表达式将所有数字（包括大整数）转换为字符串
              return JSON.parse(str.replace(/(\d{16,})/g, '"$1"'));
            };

            const msg = parseJSONWithBigInt(event.data);
            console.log("原始消息:", event.data);
            console.log("解析后的WebSocket消息:", JSON.stringify(msg, null, 2));
            console.log("wareOutDetail字段类型:", typeof msg.wareOutDetail);
            console.log("wareOutDetail字段值:", msg.wareOutDetail);

            // 处理物料状态更新消息
            if (msg.type === "materialStatusUpdate") {
              console.log("处理物料状态更新消息:", msg);
              // 仅提取wareOutDetail字段（核心匹配字段）
              const targetWareOutDetail = msg.wareOutDetail;
              if (!targetWareOutDetail) {
                console.warn("WebSocket消息缺少wareOutDetail字段，跳过处理");
                return;
              }
              console.log("待过滤的wareOutDetail:", targetWareOutDetail);
              console.log(
                "待过滤的wareOutDetail类型:",
                typeof targetWareOutDetail,
              );

              // 核心：纯前端无感过滤，无任何页面刷新/重新请求
              logs.value = logs.value
                .map((log) => {
                  console.log(
                    `处理出库单 ${log.id}，原始物料数:`,
                    log.wareOutDetailList.length,
                  );

                  // 过滤当前出库单中匹配的物料（兼容多字段）
                  const remainMaterials = log.wareOutDetailList.filter(
                    (material) => {
                      // 匹配逻辑：物料的id/wareOutDetailId/wareOutDetail 任一等于目标值则过滤
                      const matIds = [
                        material.id,
                        material.wareOutDetailId,
                        material.wareOutDetail,
                      ].filter(Boolean); // 过滤空值
                      const isMatched = matIds.includes(targetWareOutDetail);
                      console.log(
                        `物料 ${material.materialNo} 匹配ID列表:`,
                        matIds,
                        "匹配结果:",
                        isMatched,
                      );
                      return !isMatched;
                    },
                  );

                  console.log(
                    `出库单 ${log.id} 过滤后剩余物料数:`,
                    remainMaterials.length,
                  );

                  // 如果剩余物料为空，返回null（后续过滤掉整个出库单）
                  if (remainMaterials.length === 0) {
                    console.log(
                      `出库单 ${log.id} 所有物料已完成，移除该出库单`,
                    );
                    return null;
                  }

                  // 否则返回更新后的出库单（仅保留未完成物料）
                  return {
                    ...log,
                    wareOutDetailList: remainMaterials,
                  };
                })
                // 过滤掉所有空的出库单
                .filter((log) => log !== null);

              // 关键：强制Vue响应式更新，无任何页面刷新
              await nextTick();
              // 重新赋值触发响应式，视觉上无感知
              logs.value = JSON.parse(JSON.stringify(logs.value));

              // 清理提交状态和失败标记
              submittingMaterials.value = submittingMaterials.value.filter(
                (id) => id !== targetWareOutDetail,
              );
              localStorage.removeItem(`pls_fail_${targetWareOutDetail}`);

              console.log("WebSocket处理完成，当前日志列表:", logs.value);
            }
          } catch (e) {
            console.error("解析WebSocket消息失败:", e);
          }
        };

        webSocket.onclose = () => {
          console.log("WebSocket连接关闭，5秒后重连...");
          setTimeout(initWebSocket, 5000);
        };

        webSocket.onerror = (error) => {
          console.error("WebSocket连接错误:", error);
          startPollForFallback();
        };
      } catch (e) {
        console.error("初始化WebSocket失败:", e);
        startPollForFallback();
      }
    };

    // WebSocket失败时兜底轮询 - 仅前端过滤，不刷新
    const startPollForFallback = () => {
      if (pollTimers.value.fallback) clearInterval(pollTimers.value.fallback);
      // 兜底轮询仅检查状态，前端过滤，不重新拉取列表
      pollTimers.value.fallback = setInterval(async () => {
        try {
          // 仅检查提交中的物料状态，前端过滤
          if (submittingMaterials.value.length === 0) return;
          const checkIds = [...submittingMaterials.value];
          for (const id of checkIds) {
            const res = await materialPreparationApi.getMaterialStatus(id);
            if (res.data.planStatus === 2) {
              submittingMaterials.value = submittingMaterials.value.filter(
                (item) => item !== id,
              );
              localStorage.removeItem(`pls_fail_${id}`);
              // 前端过滤
              logs.value = logs.value
                .map((log) => {
                  const remain = log.wareOutDetailList.filter((m) => {
                    const matId = m.id || m.wareOutDetailId || m.wareOutDetail;
                    return matId !== id;
                  });
                  return { ...log, wareOutDetailList: remain };
                })
                .filter((log) => log.wareOutDetailList.length > 0);
              nextTick(() => {
                logs.value = [...logs.value];
              });
            }
          }
        } catch (error) {
          console.error("兜底轮询失败:", error);
        }
      }, 10000);
    };

    // 生命周期
    onMounted(() => {
      getLogs();
      initWebSocket();

      // 清理过期失败标记
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("pls_fail_")) {
          const materialId = key.replace("pls_fail_", "");
          const log = logs.value.find((item) =>
            item.wareOutDetailList.some(
              (m) =>
                m.id === materialId ||
                m.wareOutDetailId === materialId ||
                m.wareOutDetail === materialId,
            ),
          );
          if (log) {
            const material = log.wareOutDetailList.find(
              (m) =>
                m.id === materialId ||
                m.wareOutDetailId === materialId ||
                m.wareOutDetail === materialId,
            );
            if (material?.status === 2) {
              localStorage.removeItem(key);
            }
          }
        }
      });
    });

    onUnmounted(() => {
      submittingMaterials.value = [];
      Object.values(pollTimers.value).forEach((timer) => clearInterval(timer));
      if (webSocket) {
        webSocket.close();
        console.log("WebSocket连接已关闭");
      }
    });

    return {
      logs,
      total,
      currentPage,
      totalPages,
      searchKeyword,
      activeFilter,
      activeEmergencyFilter,
      filterOptions,
      emergencyFilterOptions,
      expandedLogIds,
      getLogs,
      searchLogs,
      setFilter,
      setEmergencyFilter,
      changePage,
      formatTime,
      getStatusText,
      goBack,
      toggleLogExpand,
      getSupplierName,
      handleMaterialCodeClick,
      stockModalVisible,
      currentStockData,
      loadingStock,
      loadingLogs,
      closeStockModal,
      submittingMaterials,
      confirmModalVisible,
      currentMaterial,
      openConfirmModal,
      confirmSubmitMaterialStatus,
      cancelSubmitMaterialStatus,
      allocationModalVisible,
      loadingUsers,
      usersList,
      selectedUserId,
      currentAllocationMaterial,
      openAllocationModal,
      confirmAllocation,
      closeAllocationModal,
      getFailStatus,
      handleRetry,
    };
  },
};
</script>

<style scoped>
.retry-btn {
  background-color: var(--primary-color);
  color: var(--primary-text);
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 8px;
}
.retry-btn:hover {
  background-color: var(--primary-hover);
}
.pls-material-preparation-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-color);
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 64px;
  box-sizing: border-box;
}

.back-button {
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  margin-right: 16px;
}

.back-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.back-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
}

/* 内容区域 */
.content {
  flex: 1;
  padding: 20px;
  margin-top: 64px;
}

/* 搜索和筛选区域 */
.search-filter-container {
  background-color: var(--background-color);
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  box-sizing: border-box;
  width: 100%;
}

.search-container {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  box-sizing: border-box;
  width: 100%;
}

.search-input {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: var(--input-background);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
  background-color: var(--card-background);
}

.search-btn {
  padding: 10px 20px;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-color) 100%
  );
  color: var(--primary-text);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-btn:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}

/* 筛选容器 */
.filter-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 筛选导航 */
.filter-nav {
  display: flex;
  justify-content: space-between;
  overflow-x: hidden;
  padding-bottom: 4px;
  width: 100%;
  box-sizing: border-box;
}

.filter-item {
  flex: 1;
  min-width: 30%;
  padding: 6px 8px;
  background-color: var(--filter-background);
  color: var(--filter-text);
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  text-align: center;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.filter-item:hover {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.filter-item.active {
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-color) 100%
  );
  color: var(--primary-text);
}

/* 日志列表 */
.log-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 日志卡片 */
.log-card {
  background-color: var(--card-background);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.2s ease;
}

.log-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* 日志卡片头部 */
.log-card-header {
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color);
  position: relative;
}

/* 订单号+状态 行 */
.log-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  width: 100%;
}

.log-pls-no {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.log-status {
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.log-status.status-0 {
  background-color: var(--warning-background);
  color: var(--warning-text);
}

.log-status.status-1 {
  background-color: var(--success-background);
  color: var(--success-text);
}

/* 供应商+时间 行 */
.log-basic-info {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
}

/* 主机厂信息行 */
.log-factory-info {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 8px;
}

.supplier-name {
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 65%;
}

.factory-name {
  font-size: 14px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 85%;
}

.log-time {
  font-size: 12px;
  color: var(--text-secondary);
}

/* 物料信息区域 - 始终显示 */
.material-section {
  background-color: var(--card-background);
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 物料项 - 核心样式优化 */
.material-item {
  background-color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border-left: 3px solid var(--primary-color);
}

/* 物料编码和备料状态行 - 同一行显示 */
.material-status-code-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

/* 物料描述行 - 独占一行 */
.material-desc-row {
  margin-bottom: 12px;
  text-align: left;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  padding-right: 4px;
}

/* 物料编码 - 重点突出（增强点击反馈） */
.material-code-wrapper {
  flex-shrink: 0;
}

/* 备料状态 - 靠右显示 */
.material-status-wrapper {
  margin-left: auto;
}

.material-code-label {
  font-size: 12px;
  color: var(--text-secondary);
  margin-right: 4px;
}

.material-no {
  font-size: 16px;
  font-weight: 700;
  color: var(--primary-color);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: all 0.2s ease;
}

.material-no:hover {
  color: #16a3b8;
  transform: scale(1.02); /*  hover放大，增强交互感 */
}

/* 物料描述 */
.material-desc-wrapper {
  flex: 1;
  min-width: 120px;
  text-align: left;
}
/* 物料描述 */
.material-desc {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

/* 物料数量 - 重点突出 */
.material-quantity-wrapper {
  flex-shrink: 0;
  background-color: var(--filter-background);
  padding: 6px 12px;
  border-radius: 6px;
}

.quantity-label {
  font-size: 12px;
  color: var(--primary-color);
  margin-right: 4px;
}

.material-quantity {
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-text);
}

/* 箱数/尾箱数 + 状态操作 */
.material-carton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

/* 物料状态 - 单独一行 */
.material-status-wrapper {
  flex-shrink: 0;
}

/* 数量和箱数/尾箱数行 - 合并在同一行 */
.material-quantity-carton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

/* 操作按钮区域 */
.material-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
}
/* 物料状态 - 与编码描述同行 */
.material-status-wrapper {
  flex-shrink: 0; /* 状态不会收缩，确保总是完整显示 */
  margin-left: auto;
}

/* 数量和箱数/尾箱数行 - 合并在同一行 */
.material-quantity-carton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

/* 操作按钮区域 */
.material-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
}

/* 箱数信息 - 重点突出 */
.material-quantity-wrapper {
  flex-shrink: 0;
  background-color: var(--filter-background);
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.carton-label {
  font-size: 12px;
  color: var(--text-secondary);
  margin-right: 4px;
}

.material-carton-info {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

/* 状态和操作按钮 */
.material-status-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.material-status {
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 14px;
}

.status-pending {
  background-color: var(--warning-background);
  color: var(--warning-text);
}

.status-ready {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

/* 备料中状态 */
.status-preparing {
  background-color: var(--info-background);
  color: var(--info-text);
}

/* 分配按钮 */
.allocate-btn {
  padding: 6px 16px;
  background-color: var(--primary-color);
  color: var(--primary-text);
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 48%; /* 确保两个按钮宽度相同 */
}

.allocate-btn:hover {
  background-color: var(--primary-hover);
}

/* 备料完成按钮 */
.confirm-btn {
  padding: 6px 16px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 48%; /* 确保两个按钮宽度相同 */
}

.confirm-btn:hover {
  background-color: var(--primary-hover);
  transform: translateY(-1px);
}

.cancel-btn {
  padding: 6px 12px;
  background-color: var(--filter-background);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  margin-right: 10px;
}

.cancel-btn:hover {
  background-color: var(--filter-hover);
  transition: all 0.2s ease;
}

.confirm-btn:disabled {
  background-color: var(--disabled-color);
  cursor: not-allowed;
  transform: none;
  opacity: 0.8;
}

/* 加载旋转动画 */
.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-material {
  color: var(--text-secondary);
  font-size: 14px;
  padding: 20px;
  text-align: center;
  background-color: var(--card-background);
  border-radius: 8px;
}

/* 失败原因 - 始终显示 */
.fail-section {
  padding: 12px 20px;
  background-color: rgba(239, 68, 68, 0.05);
  border-top: 1px solid rgba(239, 68, 68, 0.2);
}

.fail-desc {
  display: inline-block;
  padding: 8px 12px;
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.4;
}

/* 无数据提示 */
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.no-data-img {
  width: 100px;
  height: 100px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-data p {
  font-size: 16px;
  margin: 0;
}

/* 分页 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  gap: 16px;
  padding: 16px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.page-btn {
  padding: 8px 20px;
  background-color: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: var(--primary-color);
  font-weight: 600;
}

/* 加载状态样式 */
.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4); /* 半透明黑色背景，实现暗淡效果 */
  z-index: 9999; /* 确保在最上层显示 */
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(35, 177, 192, 0.2);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  color: var(--text-secondary);
}

/* 物料库存数据弹窗 - 修复样式穿透（关键！） */
.stock-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* 确保弹窗在最上层 */
  backdrop-filter: blur(2px); /* 增加毛玻璃效果，提升视觉 */
}

.stock-modal {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.stock-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
}

.stock-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.close-btn:hover {
  color: var(--text-primary);
}

.stock-modal-body {
  padding: 20px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0; /* 增加加载区域高度 */
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 4px solid rgba(35, 177, 192, 0.2);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.stock-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stock-item {
  display: flex;
  align-items: center; /* 改为垂直居中，更美观 */
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}

.stock-item:last-child {
  border-bottom: none;
}

.stock-item .label {
  font-weight: 500;
  color: var(--text-secondary);
  min-width: 80px;
  flex-shrink: 0;
  font-size: 14px;
}

.stock-item .value {
  color: var(--text-primary);
  flex: 1;
  word-break: break-word;
  font-size: 16px; /* 增大数值字体 */
  font-weight: 500;
}

.stock-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
}

/* 分配用户弹窗样式 */
.user-list-container {
  margin-top: 20px;
}

.user-list-container h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-primary);
}

.user-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 4px;
}

.user-item {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background-color: rgba(35, 177, 192, 0.12);
}

.user-item.active {
  background-color: rgba(35, 177, 192, 0.12);
  border-left: 3px solid var(--primary-color);
}

.user-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-name {
  font-weight: 500;
  color: var(--text-primary);
}

.user-account {
  font-size: 13px;
  color: var(--text-secondary);
}

.empty-users {
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .content {
    padding: 12px;
  }

  .search-container {
    flex-direction: column;
    gap: 8px;
  }

  .material-main-row {
    gap: 12px;
  }

  .material-no {
    font-size: 14px;
  }

  .material-quantity {
    font-size: 16px;
  }

  .material-carton-info {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 10px 12px;
    height: 56px;
  }

  .page-title {
    font-size: 16px;
  }

  .content {
    margin-top: 56px;
    padding: 8px;
  }

  .log-card-header {
    padding: 12px 16px;
  }

  .log-pls-no {
    font-size: 14px;
  }

  .material-section {
    padding: 12px 16px;
  }

  .material-item {
    padding: 12px;
  }

  .material-carton-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .material-status-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
